import { useEffect, useRef, useState } from 'react'
import { inject, observer } from 'mobx-react'
import Input from '../../common/Input'
import { FieldGroup, Field, FieldLabel } from '../../common/Field'
import { selectionTypes } from '../../../constants'
import { PhoneInput, getCountry } from 'react-international-phone'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon } from '@hugeicons/core-free-icons'
import { isPhoneValid, isEmailValid } from '../../../utils'
import 'react-international-phone/style.css'

const CountrySelectorButton = ({ rootProps, country, onOpenChange }) => {
  const isOpen = rootProps['aria-expanded']

  useEffect(() => {
    onOpenChange(isOpen)
  }, [isOpen, onOpenChange])

  return (
    <button
      {...rootProps}
      type='button'
      className='react-international-phone-country-selector-button'
      style={{ padding: '0 8px', fontSize: '13px' }}
    >
      {country ? `+${country.dialCode}` : null}
      <span style={{ marginLeft: '4px' }}><HugeiconsIcon icon={ArrowDown01Icon} size={10} /></span>
    </button>
  )
}

const ContactDetails = ({ selectionStore }) => {
  const item = selectionStore.item ? selectionStore.item : null
  if (!item || typeof (item.contact_detail) !== 'object') return null

  const [country, setCountry] = useState(() => (
    (item?.contact_detail?.country && getCountry({ field: 'iso2', value: item.contact_detail.country })) ||
    getCountry({ field: 'iso2', value: 'us' })
  ))
  const [phoneInvalid, setPhoneInvalid] = useState(false)
  const [emailInvalid, setEmailInvalid] = useState(false)

  const phoneTouchedRef = useRef(false)
  const [phoneFocused, setPhoneFocused] = useState(false)
  const [phoneHovered, setPhoneHovered] = useState(false)
  const [countrySelectorOpen, setCountrySelectorOpen] = useState(false)
  const [emailFocused, setEmailFocused] = useState(false)
  const [emailHovered, setEmailHovered] = useState(false)
  const [email, setEmail] = useState(item?.contact_detail?.email || '')
  const [phone, setPhone] = useState(item?.contact_detail?.phone || '')

  if (!item) return null

  if (!item.type === selectionTypes.ZIP_CODE_BOUNDARY &&
    !item.type === selectionTypes.BOUNDARY && !item.type === selectionTypes.GEOPOLYGON &&
    !item.type === selectionTypes.TARGET_GROUP && item.type === selectionTypes.MARKER) return null

  const flushSave = () => {
    if (emailInvalid) {
      item.updateContactDetail('email', '')
    }
    if (phoneInvalid) {
      item.updateContactDetail('phone', '')
    }
    item.debouncedSaveContactDetail.flush()
  }

  const invalidFieldStyle = {
    '--input-bg': 'color-mix(in srgb, var(--input-error, #e11d48) 8%, white)',
    '--input-border': 'var(--input-error, #e11d48)',
    width: '100%'
  }

  return (
    <div>
      <FieldGroup>
        <div style={{ display: 'flex', flex: 1, gap: '12px' }}>
          <Field>
            <FieldLabel>
              Given Name
            </FieldLabel>
            <Input
              placeholder='Enter given name'
              value={item?.contact_detail?.given_name || ''}
              onChange={event => item.updateContactDetail('given_name', event.target.value)}
              onBlur={flushSave}
            />
          </Field>
          <Field>
            <FieldLabel>
              Surname
            </FieldLabel>
            <Input
              placeholder='Enter surname'
              value={item?.contact_detail?.surname || ''}
              onChange={event => item.updateContactDetail('surname', event.target.value)}
              onBlur={flushSave}
            />
          </Field>
        </div>
        <Field style={{ position: 'relative' }}>
          <FieldLabel>
            Email
          </FieldLabel>
          <div
            style={{ position: 'relative', width: '100%' }}
            onMouseEnter={() => setEmailHovered(true)}
            onMouseLeave={() => setEmailHovered(false)}
          >
            <Input
              type='email'
              placeholder='sample@example.com'
              value={email || ''}
              style={emailInvalid ? invalidFieldStyle : undefined}
              onChange={event => {
                setEmail(event.target.value)
                if (isEmailValid(event.target.value)) {
                  setEmailInvalid(false)
                  item.updateContactDetail('email', event.target.value)
                } else {
                  setEmailInvalid(true)
                }
              }}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => {
                setEmailFocused(false)
                flushSave()
              }}
            />
            {emailInvalid && (emailFocused || emailHovered) && (
              <div className='field-error-tooltip' role='alert'>
                Please enter a valid email address
              </div>
            )}
          </div>
        </Field>
        <Field style={{ position: 'relative' }}>
          <FieldLabel>
            Phone
          </FieldLabel>
          <div
            style={{ position: 'relative', width: '100%' }}
            onMouseEnter={() => setPhoneHovered(true)}
            onMouseLeave={() => setPhoneHovered(false)}
          >
            <PhoneInput
              allowMaskOverflow
              className='contact-phone-input'
              style={{ width: '100%', ...(phoneInvalid ? invalidFieldStyle : {}) }}
              defaultCountry='us'
              disableDialCodeAndPrefix
              value={phone || ''}
              onChange={(phone, meta) => {
                setPhone(phone)
                if (isPhoneValid(phone)) {
                  setPhoneInvalid(false)
                  item.updateContactDetail('phone', phone)
                } else if (phoneTouchedRef.current) {
                  setPhoneInvalid(true)
                }
                setCountry(meta.country)
                if (meta.country.iso2 !== item.contact_detail.country) {
                  item.updateContactDetail('country', meta.country.iso2)
                }
              }}
              onFocus={() => {
                phoneTouchedRef.current = true
                setPhoneFocused(true)
              }}
              onBlur={() => {
                setPhoneFocused(false)
                flushSave()
              }}
              countrySelectorStyleProps={{
                renderButtonWrapper: ({ rootProps }) => (
                  <CountrySelectorButton rootProps={rootProps} country={country} onOpenChange={setCountrySelectorOpen} />
                )
              }}
            />
            {phoneInvalid && (phoneFocused || phoneHovered) && !countrySelectorOpen && (
              <div className='field-error-tooltip' role='alert'>
                Please enter a valid phone number
              </div>
            )}
          </div>
        </Field>
        <Field>
          <FieldLabel>
            Address
          </FieldLabel>
          <Input
            value={item?.contact_detail?.address || ''}
            onChange={event => item.updateContactDetail('address', event.target.value)}
            onBlur={flushSave}
          />
        </Field>
      </FieldGroup>
    </div>
  )
}

export default inject('selectionStore')(observer(ContactDetails))
